import ApiService from '@/services/apiService';
import { mapState, mapGetters } from 'vuex';
import multiSearchAlarmLogs from '@/mixins/multiSearchAlarmLogs';
import listOptionsPersistenceMixin from '@/components/custom/listOptionsPersistenceMixin';
import iconsMixin from '@/components/core/dtwin/state/bridge/iconsMixin';
import { userItemsPerListMixin } from '@/utils/vuex';
import { localizeDate } from '@/utils/date';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
const defaultItemsPerPage = 10;
const FETCH_DEBOUNCE_MS = 300;
const defaultTableOptions = {
  sortBy: ['timestamp'],
  sortDesc: [true],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreAlarmLogsList',
  mixins: [listOptionsPersistenceMixin, iconsMixin, userItemsPerListMixin, multiSearchAlarmLogs],
  props: {
    accounts: {
      type: Array,
      default: () => [],
      required: false
    },
    predefinedSearch: {
      type: Array,
      default: () => [],
      required: false
    },
    // if used in the device-type list, do not restore the last filter from the virtual-device-list
    // should show ALL unfiltered devices by default
    restoreFilter: {
      type: Boolean,
      required: false,
      default: () => true
    },
    containerClass: {
      type: String,
      default: () => 'n20-mainrow',
      required: false
    },
    initOperator: {
      type: String,
      default: 'AND',
      required: false
    },
    staticSearchItems: {
      type: Array,
      default: () => [],
      required: false
    }
  },
  data: function () {
    return {
      commentDialogIsOpen: false,
      commentText: '',
      expanded: [],
      disableFetchOnCreatedMixin: true,
      lastSearch: null,
      searchItems: [],
      searchOperator: 'AND',
      listPersistencePath: 'AlarmLogs.list',
      searchMode: 'multi',
      isFetching: true,
      tableOptions: {
        ...defaultTableOptions
      },
      defaultTableOptions: {
        ...defaultTableOptions
      },
      selected: [],
      list: [],
      serverItemsLength: -1,
      searchItem: null,
      selectedRows: [],
      cancelTokenSource: null,
      searchPersistKey: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions', 'language']),
    ...mapGetters({
      getAccountById: 'app/getAccountById',
      sessionUserId: 'authentication/userId'
    }),
    isEmptyList() {
      return this.searchItems.length === 0 &&
      // if no search is active
      this.list.length === 0 &&
      // if no items are in the list
      this.serverItemsLength <= 0 &&
      // if total of items is 0
      this.isFetching === false // if the list is not being right now
      ;
    },
    canComment() {
      return this.selectedRows.length > 0;
    },
    tableHeaders() {
      return [{
        text: this.$t('AlarmLogs.table.headers.status'),
        value: 'isResolved',
        sortable: true,
        width: '70px'
      }, {
        text: this.$t('AlarmLogs.table.headers.severity'),
        value: 'severity',
        sortable: true,
        width: '50px'
      }, {
        text: this.$t('Common.id'),
        value: 'id',
        sortable: true,
        width: '50px'
      }, {
        text: this.$t('AlarmLogs.table.headers.date'),
        value: 'timestamp',
        sortable: true,
        width: '150px'
      }, {
        text: this.$t('AlarmLogs.table.headers.logCategory'),
        value: 'logCategory',
        sortable: true,
        width: '150px'
      }, {
        text: this.$t('AlarmLogs.table.headers.message'),
        value: 'message',
        sortable: true
      }, {
        text: this.$t('AlarmLogs.table.headers.account'),
        value: 'tenantId',
        sortable: true
      }, {
        text: this.$t('AlarmLogs.table.headers.originEntity'),
        value: 'virtualDevice.title',
        sortable: false
        // hide: 'mdAndDown',
      }, {
        text: this.$t('AlarmLogs.table.headers.operationalStatus'),
        value: 'virtualDevice.operationalStatus',
        sortable: true
      }].filter(item => {
        return !item.hide || !this.$vuetify.breakpoint[item.hide];
      });
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$t('Common.rowsPerPage'),
        itemsPerPageOptions: [10, defaultItemsPerPage, 20, 50, 100, 200]
      };
    },
    operationalStatus() {
      return {
        0: this.$t('NiotaClassic.Devices.form.unset'),
        1: this.$t('NiotaClassic.Devices.form.inTransit'),
        2: this.$t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$t('NiotaClassic.Devices.form.inOperation'),
        4: this.$t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$t('NiotaClassic.Devices.form.decommissioned')
      };
    }
  },
  watch: {
    initOperator: {
      handler: function (val) {
        this.searchOperator = val;
      }
    },
    accounts: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList(true);
    }, 500)
  },
  created() {
    this.searchPersistKey = this.$route.path;
    const rawValue = this.$store.state.app[this.searchPersistKey];
    const {
      search
    } = rawValue ? JSON.parse(rawValue) : {};
    this.searchItems = search || [];
    this.searchOperator = this.initOperator;
  },
  methods: {
    get,
    localizeDate,
    getMsg(message) {
      if (message === 'ALARM_LOG_METRIC_RESOLVED') {
        return this.$i18n_t('AlarmLogs.resolvedMessage');
      }
      return message;
    },
    closeCommentDialog() {
      this.commentDialogIsOpen = false;
    },
    saveComment() {
      const logIds = this.selectedRows.map(row => row.id).filter(id => id);
      if (logIds.length === 0) {
        return;
      }
      const comments = logIds.map(logId => ({
        parentLogId: logId,
        message: this.commentText
      }));
      ApiService.createAlarmLogsCommentsBatch(comments).catch(() => {
        this.$snack(this.$t('AlarmLogs.snacks.commentFailed'), 'error');
      }).finally(() => {
        this.fetchList();
        this.commentText = '';
        this.closeCommentDialog();
      });
    },
    transformSearchPayloadForApi(searchItems) {
      const KEYS_ARRAY = ['tags', 'virtualDeviceGroups'];
      const KEYS_NUMBER = ['logId', 'virtualDeviceId', 'virtualDeviceOperationalStatus'];
      const transformed = searchItems.reduce((acc, {
        variable,
        search
      }) => {
        // equalize search value
        const searchValue = search.value || search;

        // consolidate to array and/or parse number to int
        if (KEYS_ARRAY.includes(variable)) {
          acc[variable] = acc[variable] || [];
          acc[variable].push(searchValue);
        } else {
          acc[variable] = KEYS_NUMBER.includes(variable) ? parseInt(searchValue, 10) : searchValue;
        }
        return acc;
      }, {});

      // restructuring result to array of objects
      return Object.entries(transformed).map(([variable, search]) => ({
        variable,
        search
      }));
    },
    sanitizeOrderBy(orderBy) {
      // order keys for the API are sometimes different than the table header keys
      switch (orderBy) {
        case 'id':
          return 'logId';
        case 'virtualDevice.operationalStatus':
          return 'virtualDeviceOperationalStatus';
        default:
          return orderBy || 'timestamp';
      }
    },
    fetchList: debounce(function () {
      this.isFetching = true;
      this.list = []; // clear list while loading

      let searchItems = (this.predefinedSearch || []).concat(this.searchItems).map(item => ({
        variable: item.param,
        // support item.search.value === null, so do not simply have a >search.value || search< expression
        search: (item.search || {}).value === undefined ? item.search : item.search.value,
        key: item.key
      }));
      if (this.accounts.length > 0) {
        searchItems.push({
          variable: 'tenantIds',
          search: this.accounts
        });
      }
      const sanitizedSearch = [...this.transformSearchPayloadForApi(searchItems), ...this.staticSearchItems];
      const {
        page,
        itemsPerPage,
        sortBy,
        sortDesc
      } = this.tableOptions;
      const sortOrder = {
        variable: this.sanitizeOrderBy(sortBy[0]),
        direction: sortDesc[0] ? 'DESC' : 'ASC'
      };
      ApiService.fetchAlarmLogs(page, itemsPerPage, sanitizedSearch, sortOrder).then(response => {
        this.list = response.logs;
        this.serverItemsLength = response.pagination.total;
      }).catch(() => {
        this.$snack(this.$t('AlarmLogs.snacks.fetchLogsFailed'), 'error');
      }).finally(() => {
        this.isFetching = false;
      });
    }, FETCH_DEBOUNCE_MS),
    setSelected(rows) {
      this.selectedRows = rows;
    },
    searchUpdated(search, searchOperator) {
      const storeSearch = JSON.stringify({
        search,
        searchOperator
      });
      this.$store.commit('app/setField', {
        field: this.searchPersistKey,
        value: storeSearch
      });
      this.searchItems = search;
      this.searchOperator = searchOperator;
      this.tableOptions.page = 1; // reset page when search changes
      this.fetchList();
    },
    getTenantNameById(tenantId) {
      const account = this.getAccountById(tenantId);
      return account ? account.name : tenantId;
    },
    getBridgeMonitoringTitle(item) {
      // parse "BRIDGE#1-RULE#19" format
      const originId = item.originId || '';
      const match = originId.match(/BRIDGE#(\d+)-RULE#(\d+)/);
      if (match) {
        const [, bridgeId, ruleId] = match;
        return `Bridge ${bridgeId} - Rule ${ruleId}`;
      }

      // fallback if format doesn't match
      return `Bridge ${item.referencedOriginId || '?'} - Rule ${originId}`;
    },
    getBridgeMonitoringLink(item) {
      // parse "BRIDGE#1-RULE#19" to extract bridge ID
      const originId = item.originId || '';
      const match = originId.match(/BRIDGE#(\d+)-RULE#(\d+)/);
      const tenantId = item.tenantId;
      if (match) {
        const [, bridgeId] = match;
        return `/connectors/edit/${tenantId}/${bridgeId}`;
      }

      // fallback to referencedOriginId if format doesn't match
      const bridgeId = item.referencedOriginId || '0';
      return `/connectors/edit/${tenantId}/${bridgeId}`;
    }
  }
};